import { ofType } from 'redux-observable';
import { Observable } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, of } from 'rxjs';
import { getLoginRequestHeader, localStorageHelper } from '../../utils';
import {
    allCurrenciesSuccess,
    allCurrenciesByBrandSuccess,
    apiError,
    changeDefaultPasswordSuccess,
    changeNameSuccess,
    changePasswordSuccess,
    getAllCountriesSuccess,
    loginSuccess,
    logoutLocal,
    sessionSuccess,
    showErrorNotification,
    timezoneSuccess,
    updatePreferencesSuccess,
    UserTypes,
    DateOfLastRefrasheSuccessfull,
    getAllGamesByUserSuccess,
    getAllGameConfigurationsByUserSuccess,
    sessionExpired,
} from '../actions/user-actions';
import { CasinoPerformanceReportTypes } from '../actions/backoffice/casino-performance-report-actions';
import { IAction, IStateObservable } from '../../helpers/interfaces';
import { UserConfigurationResource } from '../../utils/AuthoritiesMap';
import axios from 'axios';
import { SERVER_URL } from '../../utils/config';
import { GameHistoryTypes } from '../actions/backoffice/game-history-actions';
import { GamesReportTypes } from '../actions/backoffice/games-report-actions';
import { PlayerReportTypes } from '../actions/backoffice/player-report-actions';
import { PlayersReportTypes } from '../actions/backoffice/players-report-actions';
import {
    isUnauthorizedResponse,
    shouldInvalidateSession,
} from '../../session/unauthorizedResponse';
import {
    createUserConfiguration,
    emptyUserConfiguration,
    getRequiredUserConfigurationResources,
    IUserConfigurationData,
} from '../../session/userConfigurationBootstrap';
import { currencyNameConverter } from '../../utils/Currency/currencyNameConverter';
import dayjs from 'dayjs';

const bootstrapXsrfToken = () =>
    from(
        axios.get(`${SERVER_URL}/session`, { headers: getLoginRequestHeader() }).catch(() => null),
    );

const getSessionRequest = () =>
    from(axios.get(`${SERVER_URL}/session`, { headers: getLoginRequestHeader() }));

const USER_CONFIGURATION_ENDPOINTS: Record<UserConfigurationResource, string> = {
    [UserConfigurationResource.ALL_CURRENCIES]: '/currencies/all',
    [UserConfigurationResource.CURRENCIES_BY_BRAND]: '/currencies/by-brand',
    [UserConfigurationResource.TIMEZONES]: '/time-zone/list',
    [UserConfigurationResource.COUNTRIES]: '/country/all',
    [UserConfigurationResource.ALL_GAMES]: '/configuration/game',
};

const loadUserConfiguration = (
    authorities: string[],
    headers: any,
): Observable<IUserConfigurationData> => {
    const resources = getRequiredUserConfigurationResources(authorities);

    if (!resources.length) {
        return of(emptyUserConfiguration());
    }

    return forkJoin(
        resources.map((resource) =>
            from(
                axios.get(`${SERVER_URL}${USER_CONFIGURATION_ENDPOINTS[resource]}`, { headers }),
            ).pipe(
                map(
                    (response) =>
                        [resource, Array.isArray(response.data) ? response.data : []] as [
                            UserConfigurationResource,
                            any[],
                        ],
                ),
            ),
        ),
    ).pipe(map(createUserConfiguration));
};

const persistUserConfiguration = (accountInfo: any, configuration: IUserConfigurationData) => {
    const storedUser = localStorageHelper.getParsed('user') || {};
    const nextUser = { ...storedUser, ...accountInfo, ...configuration };

    localStorageHelper.set('user', nextUser);
    currencyNameConverter.setCurrencies(configuration.allCurrencies);

    if (configuration.timezoneList.length) {
        localStorage.setItem('timestamp', dayjs().format());
    }
};

export const loginEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.LOGIN_REQUEST),
        mergeMap((action) => {
            const requestBody = {
                email: action.payload.email,
                password: action.payload.password,
            };

            const bootstrapXsrf = bootstrapXsrfToken();

            return bootstrapXsrf.pipe(
                mergeMap(() => {
                    const headers = getLoginRequestHeader();

                    return from(
                        axios.post(`${SERVER_URL}/account/login`, requestBody, { headers }),
                    );
                }),
                mergeMap(() =>
                    from([loginSuccess(), { type: UserTypes.INFO_REQUEST, payload: null }]),
                ),
                catchError((error) => of(apiError(error, UserTypes.LOGIN_REQUEST))),
                takeUntil(
                    action$.pipe(
                        ofType(
                            UserTypes.LOGOUT_REQUEST,
                            UserTypes.LOGOUT,
                            UserTypes.SESSION_EXPIRED,
                        ),
                    ),
                ),
            );
        }),
    );

export const sessionEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.SESSION_REQUEST),
        exhaustMap((action) =>
            getSessionRequest().pipe(
                mergeMap(() => {
                    const nextActions: IAction[] = [
                        sessionSuccess(),
                        {
                            type: UserTypes.INFO_REQUEST,
                            payload: {
                                fromSessionBootstrap: true,
                                silent: Boolean(action.payload?.silent),
                            },
                        },
                    ];

                    return from(nextActions);
                }),
                catchError((error) =>
                    of({
                        ...apiError(error, UserTypes.SESSION_REQUEST),
                        meta: {
                            sourceActionType: UserTypes.SESSION_REQUEST,
                            silent: Boolean(action.payload?.silent),
                            revalidation: Boolean(action.payload?.revalidation),
                        },
                    }),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(
                            UserTypes.LOGOUT_REQUEST,
                            UserTypes.LOGOUT,
                            UserTypes.SESSION_EXPIRED,
                        ),
                    ),
                ),
            ),
        ),
    );

export const infoEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.INFO_REQUEST),
        switchMap((action) => {
            const headers = getLoginRequestHeader();
            const fromPromise = from(axios.get(`${SERVER_URL}/account/info`, { headers }));

            return fromPromise.pipe(
                switchMap((response: any) =>
                    loadUserConfiguration(
                        Array.isArray(response.data.authorities) ? response.data.authorities : [],
                        headers,
                    ).pipe(
                        map((configuration) => {
                            persistUserConfiguration(response.data, configuration);

                            return {
                                type: UserTypes.INFO_SUCCESS,
                                payload: {
                                    ...response.data,
                                    configuration,
                                },
                            };
                        }),
                    ),
                ),
                catchError((error) =>
                    of({
                        ...apiError(error, UserTypes.INFO_REQUEST),
                        meta: {
                            sourceActionType: UserTypes.INFO_REQUEST,
                            silent: Boolean(action.payload?.silent),
                        },
                    }),
                ),
                takeUntil(
                    action$.pipe(
                        ofType(
                            UserTypes.LOGOUT_REQUEST,
                            UserTypes.LOGOUT,
                            UserTypes.SESSION_EXPIRED,
                        ),
                    ),
                ),
            );
        }),
    );

export const logoutEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.LOGOUT_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader('application/json');

            return from(
                axios.post(`${SERVER_URL}/account/logout`, null, {
                    headers,
                }),
            ).pipe(
                mergeMap(() => of(logoutLocal())),
                catchError((error) =>
                    of(
                        {
                            type: UserTypes.LOGOUT_FAILED,
                            payload: error,
                        },
                        logoutLocal(),
                    ),
                ),
            );
        }),
    );

export const allCurrenciesEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.GET_ALL_CURRENCIES_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();
            const fromPromise = from(axios.get(`${SERVER_URL}/currencies/all`, { headers }));

            return fromPromise.pipe(
                map((response) => {
                    return allCurrenciesSuccess({
                        response: response.data,
                        source: UserTypes.GET_ALL_CURRENCIES_REQUEST,
                    });
                }),
                catchError((error) => of(apiError(error, UserTypes.GET_ALL_CURRENCIES_REQUEST))),
            );
        }),
    );

export const allCurrenciesByOperatorEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.GET_ALL_CURRENCIES_BY_BRAND_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();
            const fromPromise = from(axios.get(`${SERVER_URL}/currencies/by-brand`, { headers }));

            return fromPromise.pipe(
                map((response) => {
                    return allCurrenciesByBrandSuccess({
                        response: response.data,
                        source: UserTypes.GET_ALL_CURRENCIES_BY_BRAND_REQUEST,
                    });
                }),
                catchError((error) =>
                    of(apiError(error, UserTypes.GET_ALL_CURRENCIES_BY_BRAND_REQUEST)),
                ),
            );
        }),
    );

export const timezoneEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.TIME_ZONE_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();
            const fromPromise = from(axios.get(`${SERVER_URL}/time-zone/list`, { headers }));

            return fromPromise.pipe(
                map((response) => {
                    localStorageHelper.setChainedValue('user.timezoneList', response.data);
                    return timezoneSuccess({
                        response: response.data,
                        source: UserTypes.TIME_ZONE_REQUEST,
                    });
                }),
                catchError((error) => of(apiError(error, UserTypes.TIME_ZONE_REQUEST))),
            );
        }),
    );

export const countriesEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.GET_ALL_COUNTRIES_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();
            const fromPromise = from(axios.get(`${SERVER_URL}/country/all`, { headers }));

            return fromPromise.pipe(
                map((response) => {
                    localStorageHelper.setChainedValue('user.countries', response.data);

                    return getAllCountriesSuccess({
                        response: response.data,
                        source: UserTypes.GET_ALL_COUNTRIES_REQUEST,
                    });
                }),
                catchError((error) => of(apiError(error, UserTypes.GET_ALL_COUNTRIES_REQUEST))),
            );
        }),
    );

export const updatePreferencesEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.UPDATE_PREFERENCES_REQUEST),
        mergeMap((action) => {
            const currentPreferences = localStorageHelper.getChainedValue('user.preferences');
            const headers = getLoginRequestHeader('application/json');
            const preferencesToUpdate = Object.keys(action.payload);

            preferencesToUpdate.forEach(
                (preference) => (currentPreferences[preference] = action.payload[preference]),
            );

            const fromPromise = from(
                axios.post(`${SERVER_URL}/account/update-preferences`, currentPreferences, {
                    headers,
                }),
            );

            return fromPromise.pipe(
                map(() => updatePreferencesSuccess(currentPreferences)),
                catchError((error) => of(apiError(error, UserTypes.UPDATE_PREFERENCES_REQUEST))),
            );
        }),
    );

export const changeUserNameEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.CHANGE_NAME_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader('application/json');
            const fromPromise = from(
                axios.post(`${SERVER_URL}/account/change-name`, action.payload, {
                    headers,
                }),
            );

            return fromPromise.pipe(
                map((response) => changeNameSuccess(response.data)),
                catchError((error) => of(apiError(error, UserTypes.CHANGE_NAME_REQUEST))),
            );
        }),
    );

export const changeUserPasswordEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.CHANGE_PASSWORD_REQUEST),
        mergeMap((action) => {
            const headers = getLoginRequestHeader('application/json');
            const fromPromise = from(
                axios.post(`${SERVER_URL}/account/change-password`, action.payload, { headers }),
            );

            return fromPromise.pipe(
                map((response) => changePasswordSuccess(response.data)),
                catchError((error) => of(apiError(error, UserTypes.CHANGE_PASSWORD_REQUEST))),
            );
        }),
    );

export const changeDefaultPasswordEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.CHANGE_DEFAULT_PASSWORD_REQUEST),
        mergeMap((action) => {
            const bootstrapXsrf = from(
                axios
                    .get(`${SERVER_URL}/session`, { headers: getLoginRequestHeader() })
                    .catch(() => null),
            );

            return bootstrapXsrf.pipe(
                mergeMap(() => {
                    const headers = getLoginRequestHeader('application/json');

                    return from(
                        axios.post(`${SERVER_URL}/change-password`, action.payload, {
                            headers,
                        }),
                    );
                }),
                map((response) => changeDefaultPasswordSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, UserTypes.CHANGE_DEFAULT_PASSWORD_REQUEST)),
                ),
            );
        }),
    );

function extractErrorMessage(payload: any): string {
    if (payload?.response?.status === 429 || payload?.xhr?.status === 429) {
        return 'Your account is temporarily locked for security reasons. Please try again in 10 minutes or contact support.';
    }
    if (typeof payload?.response === 'string') {
        try {
            const parsed = JSON.parse(payload.response);
            return extractErrorMessage({ response: parsed });
        } catch {
            return payload.response;
        }
    }
    if (payload?.response?.data?.error?.message) {
        return payload.response.data.error.message;
    }
    if (payload?.response?.data?.error) {
        return payload.response.data.error;
    }
    if (payload?.response?.error?.message) {
        return payload.response.error.message;
    }
    if (payload?.response?.data?.message) {
        return payload.response.data.message;
    }
    if (payload?.response?.message) {
        return payload.response.message;
    }
    if (payload?.message) {
        return payload.message;
    }
    return 'An unknown error occurred.';
}

export const errorEpic = (action$: Observable<IAction>, state$: IStateObservable) =>
    action$.pipe(
        ofType(UserTypes.API_ERROR),
        mergeMap((action) => {
            const { payload } = action;
            const actions = [];
            const sourceActionType = action.meta?.sourceActionType;
            const isUnauthorized = isUnauthorizedResponse(payload);
            const isLoginFailure = sourceActionType === UserTypes.LOGIN_REQUEST;

            if (
                isUnauthorized &&
                !isLoginFailure &&
                shouldInvalidateSession(state$.value.userReducer.sessionStatus)
            ) {
                actions.push(sessionExpired(payload));
            }

            const shouldSuppressNotification =
                (isUnauthorized && !isLoginFailure) || Boolean(action.meta?.silent);

            if (!shouldSuppressNotification) {
                const errorMessage = extractErrorMessage(payload);
                actions.push(showErrorNotification(errorMessage));
            }

            if (sourceActionType) {
                actions.push({
                    type: sourceActionType.replace('_REQUEST', '_FAILED'),
                    payload,
                    meta: action.meta,
                });
            }

            return of(...actions);
        }),
    );

export const dateOfLastRefreshEpic = (action$: Observable<any>) =>
    action$.pipe(
        ofType(
            CasinoPerformanceReportTypes.CASINO_PERFORMANCE_REPORT_REQUEST,
            GameHistoryTypes.GAME_HISTORY_REQUEST,
            GamesReportTypes.GAMES_REPORT_REQUEST,
            PlayerReportTypes.PLAYER_REPORT_REQUEST,
            PlayersReportTypes.PLAYERS_REPORT_REQUEST,
        ),
        mergeMap(() => {
            return from(
                axios.get(`${SERVER_URL}/report-audit`, {
                    headers: getLoginRequestHeader('application/json'),
                }),
            ).pipe(
                map((response) => DateOfLastRefrasheSuccessfull(response.data)),
                catchError((error) => of(apiError(error, UserTypes.DATE_OF_LAST_REFRESH_REQUEST))),
            );
        }),
    );

export const allGamesByUserEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.GET_ALL_GAMES_BY_USER_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();
            return from(axios.get(`${SERVER_URL}/games/all`, { headers })).pipe(
                map((response) => getAllGamesByUserSuccess(response.data)),
                catchError((error) => of(apiError(error, UserTypes.GET_ALL_GAMES_BY_USER_REQUEST))),
            );
        }),
    );

export const allGameConfigurationsEpic = (action$: Observable<IAction>) =>
    action$.pipe(
        ofType(UserTypes.GET_ALL_GAME_CONFIGURATIONS_BY_USER_REQUEST),
        mergeMap(() => {
            const headers = getLoginRequestHeader();
            return from(axios.get(`${SERVER_URL}/games/configurations`, { headers })).pipe(
                map((response) => getAllGameConfigurationsByUserSuccess(response.data)),
                catchError((error) =>
                    of(apiError(error, UserTypes.GET_ALL_GAME_CONFIGURATIONS_BY_USER_REQUEST)),
                ),
            );
        }),
    );
